import React, { useState, useContext } from "react";
import {
  Box,
  TextField,
  IconButton,
  Snackbar,
  Alert,
  Grid,
  Typography,
  Button,
  InputAdornment,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { AppContext } from "../../../context/AppContext";

const SectionDetails = () => {
  const { sections = [], refreshSections, csrfFetch } = useContext(AppContext) || {};

  const [title, setTitle] = useState("");
  const [titleError, setTitleError] = useState(false);
  const [existingError, setExistingError] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [editOpen, setEditOpen] = useState(false);
  const [editSection, setEditSection] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editSequential, setEditSequential] = useState("");

  const safeSections = Array.isArray(sections) ? sections : [];

  const handleAddSection = async () => {
    if (!title.trim()) {
      setTitleError(true);
      return;
    }

    if (existingError) {
      setErrorMessage("Esta sección ya existe.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("name", title.trim());

      const response = await csrfFetch("/categorias/secciones/agregar/", {
        method: "POST",
        body: formData,
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        setErrorMessage(result.error || "No se pudo agregar la sección.");
        return;
      }

      setSuccessMessage("Sección añadida correctamente.");
      setTitle("");
      await refreshSections();
    } catch {
      setErrorMessage("No se pudo agregar la sección.");
    }
  };

  const handleOpenEditModal = (section) => {
    setEditSection(section);
    setEditTitle(section?.name || "");
    setEditSequential(section?.sequential_code || "");
    setEditOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditOpen(false);
    setEditSection(null);
    setEditTitle("");
    setEditSequential("");
  };

  const handleSaveEdit = async () => {
    if (!editSection) return;

    if (!editTitle.trim() || !editSequential.trim()) {
      setErrorMessage("Todos los campos son obligatorios.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("name", editTitle.trim());
      formData.append("sequential_code", editSequential.trim());

      const response = await csrfFetch(`/categorias/secciones/${editSection.id}/editar/`, {
        method: "POST",
        body: formData,
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        setErrorMessage(result.error || "No se pudo actualizar la sección.");
        return;
      }

      setSuccessMessage("Sección actualizada correctamente.");
      handleCloseEditModal();
      await refreshSections();
    } catch {
      setErrorMessage("No se pudo actualizar la sección.");
    }
  };

  const handleDeleteSection = async () => {
    if (!editSection) return;

    try {
      const response = await csrfFetch(`/categorias/secciones/${editSection.id}/eliminar/`, {
        method: "DELETE",
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        setErrorMessage(result.error || "No se puede eliminar esta sección porque tiene productos asociados.");
        return;
      }

      setSuccessMessage("Sección eliminada correctamente.");
      handleCloseEditModal();
      await refreshSections();
    } catch {
      setErrorMessage("No se puede eliminar esta sección porque tiene productos asociados.");
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: "#1565C0" }}>
        Gestión de Secciones
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Nombre de la sección"
            variant="outlined"
            fullWidth
            value={title}
            onChange={(e) => {
              setTitleError(false);
              setExistingError(false);
              setTitle(e.target.value);
            }}
            error={titleError}
            helperText={titleError ? "Este campo es obligatorio" : ""}
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: title && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => {
                      setTitle("");
                      setTitleError(false);
                      setExistingError(false);
                    }}
                    size="small"
                  >
                    <CloseIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant="contained"
            color="primary"
            onClick={handleAddSection}
            startIcon={<AddIcon />}
            sx={{ width: "100%" }}
            disabled={!title.trim()}
          >
            Añadir Sección
          </Button>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              maxHeight: 250,
              overflowY: "auto",
              p: 2,
              borderRadius: 2,
              backgroundColor: "#f5f5f5",
            }}
          >
            <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
              Secciones Creadas
            </Typography>
            {safeSections.map((section) => (
              <Typography
                key={section.id}
                variant="body2"
                sx={{ mb: 1, cursor: "pointer" }}
                onClick={() => handleOpenEditModal(section)}
              >
                - {section.name}
              </Typography>
            ))}
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={editOpen} onClose={handleCloseEditModal} maxWidth="md" fullWidth>
        <DialogTitle>Editar Sección</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Secuencial"
            value={editSequential}
            onChange={(e) => setEditSequential(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            fullWidth
            label="Nombre"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            sx={{ mb: 2 }}
          />
        </DialogContent>

        <DialogActions>
          <Button color="error" onClick={handleDeleteSection}>
            Eliminar
          </Button>
          <Button color="primary" onClick={handleSaveEdit}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="success">{successMessage}</Alert>
      </Snackbar>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={3000}
        onClose={() => setErrorMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="error">{errorMessage}</Alert>
      </Snackbar>
    </Box>
  );
};

export default SectionDetails;